import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  QueryStateBoundary,
  Select,
  TabContent,
  TabList,
  TabTrigger,
  Tabs,
} from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import { formatShortDate } from "@/features/events/eventDate";
import { useFinanceSummary, useFinanceInsights } from "@/api/hooks/useFinance";
import { useExpenses, useDeleteExpense } from "@/api/hooks/useExpenses";
import {
  EXPENSE_CATEGORY_BADGE_VARIANT,
  EXPENSE_CATEGORY_LABELS,
  ExpenseCategorySchema,
  type Expense,
  type ExpenseCategory,
} from "@/api/types/expense";
import type {
  CategoryBreakdownRow,
  FinanceSummary,
  MonthlyFinanceRow,
  RecurringVendorRow,
} from "@/api/types/finance";
import { defaultFinanceRange, formatMonthLabel } from "./financeDate";
import { AddExpenseDialog } from "./AddExpenseDialog";
import { ImportExpensesPanel } from "./ImportExpensesPanel";

const CATEGORY_OPTIONS = ExpenseCategorySchema.options;

/**
 * The money-analysis home: "where did it come from, where did it go, what
 * am I paying for every month." Three sections behind one shared date-range
 * filter — Cash Flow (the default read), Insights (the narrative summary),
 * and Expenses (the ledger + CSV import). Every dollar figure here is
 * server-computed (finance/summary, finance/insights) or a raw stored
 * amount_cents (expenses) — this page only formats via centsToUsd, never
 * computes a total client-side.
 */
export function FinancesPage() {
  const [range, setRange] = useState(() => defaultFinanceRange());
  const [tab, setTab] = useState("cash-flow");

  return (
    <div className="p-6 sm:p-8" data-testid="finances-page">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-text-primary">Finances</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Cash flow, where the money's going, and what's on autopay.
          </p>
        </div>
        <DateRangeControls range={range} onChange={setRange} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabList>
          <TabTrigger value="cash-flow">Cash Flow</TabTrigger>
          <TabTrigger value="insights">Insights</TabTrigger>
          <TabTrigger value="expenses">Expenses</TabTrigger>
        </TabList>

        <TabContent value="cash-flow">
          <CashFlowTab range={range} />
        </TabContent>

        <TabContent value="insights">
          <InsightsTab range={range} />
        </TabContent>

        <TabContent value="expenses">
          <ExpensesTab range={range} />
        </TabContent>
      </Tabs>
    </div>
  );
}

// ---- Shared date-range control -----------------------------------------------

interface DateRange {
  from: string;
  to: string;
}

function DateRangeControls({
  range,
  onChange,
}: {
  range: DateRange;
  onChange: (range: DateRange) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label className="mb-1 block text-xs font-medium text-text-secondary" htmlFor="finance-from">
          From
        </label>
        <input
          id="finance-from"
          type="date"
          value={range.from}
          onChange={(e) => onChange({ ...range, from: e.target.value })}
          className="h-10 rounded-md border border-border bg-bg-card px-3 text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-text-secondary" htmlFor="finance-to">
          To
        </label>
        <input
          id="finance-to"
          type="date"
          value={range.to}
          onChange={(e) => onChange({ ...range, to: e.target.value })}
          className="h-10 rounded-md border border-border bg-bg-card px-3 text-sm"
        />
      </div>
    </div>
  );
}

// ---- Cash Flow tab -------------------------------------------------------------

function CashFlowTab({ range }: { range: DateRange }) {
  const { data: summary, isLoading, error, refetch } = useFinanceSummary(range);

  return (
    <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      {summary && (
        <div className="flex flex-col gap-6">
          <HeroStats summary={summary} />

          <MonthlyChartCard monthly={summary.monthly} />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <CategoryBreakdownCard rows={summary.by_category} />
            <RecurringCard rows={summary.recurring} />
          </div>

          <TopVendorsCard rows={summary.top_vendors} />
        </div>
      )}
    </QueryStateBoundary>
  );
}

function HeroStats({ summary }: { summary: FinanceSummary }) {
  const netPositive = summary.net_cents >= 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="finance-hero-stats">
      <StatTile
        testId="finance-stat-revenue"
        label="Revenue"
        value={centsToUsd(summary.revenue_cents)}
      />
      <StatTile
        testId="finance-stat-expenses"
        label="Expenses"
        value={centsToUsd(summary.expenses_cents)}
      />
      <StatTile
        testId="finance-stat-net"
        label="Net"
        value={centsToUsd(summary.net_cents)}
        valueClassName={netPositive ? "text-success" : "text-danger"}
        badge={{ label: netPositive ? "Positive" : "Negative", variant: netPositive ? "success" : "danger" }}
      />
    </div>
  );
}

function StatTile({
  testId,
  label,
  value,
  valueClassName,
  badge,
}: {
  testId: string;
  label: string;
  value: string;
  valueClassName?: string;
  badge?: { label: string; variant: "success" | "danger" };
}) {
  return (
    <div className="rounded-lg border border-border bg-bg-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-text-secondary">{label}</p>
        {badge && (
          <Badge variant={badge.variant} size="sm">
            {badge.label}
          </Badge>
        )}
      </div>
      <p
        className={`mt-1 font-display text-3xl font-bold ${valueClassName ?? "text-text-primary"}`}
        data-testid={`${testId}-value`}
      >
        {value}
      </p>
    </div>
  );
}

// ---- Monthly revenue-vs-expenses chart -----------------------------------------

function MonthlyChartCard({ monthly }: { monthly: MonthlyFinanceRow[] }) {
  const maxCents = Math.max(1, ...monthly.flatMap((row) => [row.revenue_cents, row.expenses_cents]));

  return (
    <Card data-testid="finance-monthly-chart">
      <CardHeader className="flex flex-row items-center justify-between border-b-0 pb-2 mb-2">
        <CardTitle>Revenue vs. expenses</CardTitle>
        <div className="flex items-center gap-4 text-xs text-text-muted">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-copper" aria-hidden="true" /> Revenue
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-danger" aria-hidden="true" /> Expenses
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {monthly.length === 0 ? (
          <p className="text-sm text-text-muted">No monthly data for this range yet.</p>
        ) : (
          <div className="flex items-end gap-4">
            {monthly.map((row) => {
              const revenuePct = Math.round((row.revenue_cents / maxCents) * 100);
              const expensesPct = Math.round((row.expenses_cents / maxCents) * 100);

              return (
                <div
                  key={row.month}
                  className="flex flex-1 flex-col items-center gap-1"
                  data-testid={`finance-monthly-bar-${row.month}`}
                >
                  <div className="flex h-32 w-full items-end justify-center gap-1.5">
                    <div
                      className="w-1/2 max-w-[1.25rem] rounded-t bg-copper"
                      style={{ height: `${revenuePct}%` }}
                      title={`Revenue: ${centsToUsd(row.revenue_cents)}`}
                    />
                    <div
                      className="w-1/2 max-w-[1.25rem] rounded-t bg-danger"
                      style={{ height: `${expensesPct}%` }}
                      title={`Expenses: ${centsToUsd(row.expenses_cents)}`}
                    />
                  </div>
                  <span
                    className={`text-xs font-medium ${row.net_cents >= 0 ? "text-success" : "text-danger"}`}
                  >
                    {centsToUsd(row.net_cents)}
                  </span>
                  <span className="text-[11px] text-text-muted">{formatMonthLabel(row.month)}</span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Spend by category ----------------------------------------------------------

function CategoryBreakdownCard({ rows }: { rows: CategoryBreakdownRow[] }) {
  const maxCents = Math.max(1, ...rows.map((row) => row.amount_cents));

  return (
    <Card data-testid="finance-category-breakdown">
      <CardHeader>
        <CardTitle>Spend by category</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-text-muted">No expenses in this range yet.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {rows.map((row) => {
              const barPct = Math.round((row.amount_cents / maxCents) * 100);
              const label =
                EXPENSE_CATEGORY_LABELS[row.category as ExpenseCategory] ?? row.category;

              return (
                <div
                  key={row.category}
                  className="flex items-center gap-3"
                  data-testid={`finance-category-row-${row.category}`}
                >
                  <span className="w-28 shrink-0 truncate text-xs text-text-secondary">{label}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-bg-muted">
                    <div className="h-full rounded-full bg-copper" style={{ width: `${barPct}%` }} />
                  </div>
                  <span className="w-10 shrink-0 text-right text-xs text-text-muted">{row.pct}%</span>
                  <span className="w-20 shrink-0 text-right text-xs font-medium text-text-primary">
                    {centsToUsd(row.amount_cents)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Recurring subscriptions ------------------------------------------------------

function RecurringCard({ rows }: { rows: RecurringVendorRow[] }) {
  return (
    <Card data-testid="finance-recurring-card">
      <CardHeader>
        <CardTitle>Recurring subscriptions</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <EmptyState
            icon="🔁"
            title="No recurring spend detected"
            description="Vendors billing on a monthly cadence show up here once a few charges land."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-border" data-testid="finance-recurring-list">
            {rows.map((row) => (
              <li
                key={row.vendor}
                className="flex items-center justify-between gap-3 py-2 text-sm"
                data-testid={`finance-recurring-row-${row.vendor}`}
              >
                <div className="flex flex-col">
                  <span className="font-medium text-text-primary">{row.vendor}</span>
                  <span className="text-xs text-text-muted">
                    {row.count} charge{row.count === 1 ? "" : "s"} · last {formatShortDate(row.last_date)}
                  </span>
                </div>
                <span className="font-medium text-text-primary">
                  {centsToUsd(row.monthly_cents)}
                  <span className="text-xs font-normal text-text-muted">/mo</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Top vendors -----------------------------------------------------------------

function TopVendorsCard({ rows }: { rows: FinanceSummary["top_vendors"] }) {
  return (
    <Card data-testid="finance-top-vendors">
      <CardHeader>
        <CardTitle>Top vendors</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-text-muted">No vendor spend in this range yet.</p>
        ) : (
          <ol className="flex flex-col divide-y divide-border">
            {rows.map((row, i) => (
              <li key={row.vendor} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="text-text-primary">
                  <span className="mr-2 text-text-muted">{i + 1}.</span>
                  {row.vendor}
                </span>
                <span className="font-medium text-text-primary">{centsToUsd(row.amount_cents)}</span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Insights tab -----------------------------------------------------------------

function InsightsTab({ range }: { range: DateRange }) {
  const { data, isLoading, isFetching, error, refetch } = useFinanceInsights(range);

  return (
    <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      {data && (
        <Card data-testid="finance-insights-card">
          <CardHeader className="flex flex-row items-center justify-between border-b-0 pb-2 mb-2">
            <div className="flex items-center gap-2">
              <CardTitle>Analysis</CardTitle>
              <Badge
                variant={data.source === "haiku" ? "info" : "secondary"}
                size="sm"
                data-testid="finance-insights-source"
              >
                {data.source === "haiku" ? "AI analysis" : "Summary"}
              </Badge>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              {isFetching ? "Refreshing..." : "Refresh analysis"}
            </Button>
          </CardHeader>
          <CardContent>
            <p
              className="whitespace-pre-line text-base leading-relaxed text-text-primary"
              data-testid="finance-narrative"
            >
              {data.narrative}
            </p>
          </CardContent>
        </Card>
      )}
    </QueryStateBoundary>
  );
}

// ---- Expenses tab -----------------------------------------------------------------

function ExpensesTab({ range }: { range: DateRange }) {
  const [categoryFilter, setCategoryFilter] = useState<ExpenseCategory | "all">("all");
  const [addOpen, setAddOpen] = useState(false);

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useExpenses({
    from: range.from,
    to: range.to,
    category: categoryFilter === "all" ? undefined : categoryFilter,
  });

  const expenses = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  return (
    <div className="flex flex-col gap-6">
      <ImportExpensesPanel />

      <div>
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-[10rem]">
            <label className="mb-1 block text-xs font-medium text-text-secondary" htmlFor="expense-category-filter">
              Category
            </label>
            <Select
              id="expense-category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as ExpenseCategory | "all")}
            >
              <option value="all">All categories</option>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {EXPENSE_CATEGORY_LABELS[c]}
                </option>
              ))}
            </Select>
          </div>
          <Button onClick={() => setAddOpen(true)}>Add expense</Button>
        </div>

        <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
          {expenses.length === 0 ? (
            <EmptyState
              icon="🧾"
              title="No expenses logged"
              description="Add one manually or import a CSV of bank/card transactions above."
            />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border bg-bg-card">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-border bg-bg-hover/50 text-xs uppercase tracking-wide text-text-muted">
                  <tr>
                    <th className="px-4 py-2 font-medium">Date</th>
                    <th className="px-4 py-2 font-medium">Vendor</th>
                    <th className="px-4 py-2 font-medium">Category</th>
                    <th className="px-4 py-2 font-medium">Amount</th>
                    <th className="px-4 py-2 font-medium"></th>
                    <th className="px-4 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {expenses.map((expense) => (
                    <ExpenseRow key={expense.id} expense={expense} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </QueryStateBoundary>

        {hasNextPage && (
          <div className="mt-4 flex justify-center">
            <Button variant="secondary" onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
              {isFetchingNextPage ? "Loading..." : "Load more"}
            </Button>
          </div>
        )}
      </div>

      <AddExpenseDialog open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}

function ExpenseRow({ expense }: { expense: Expense }) {
  const deleteExpense = useDeleteExpense();

  async function handleDelete() {
    if (!window.confirm(`Delete this ${centsToUsd(expense.amount_cents)} expense?`)) return;
    await deleteExpense.mutateAsync(expense.id);
  }

  return (
    <tr data-expense-id={expense.id}>
      <td className="px-4 py-3 text-text-secondary">{formatShortDate(expense.date)}</td>
      <td className="px-4 py-3 text-text-primary">{expense.vendor || "—"}</td>
      <td className="px-4 py-3">
        <Badge variant={EXPENSE_CATEGORY_BADGE_VARIANT[expense.category]}>
          {EXPENSE_CATEGORY_LABELS[expense.category]}
        </Badge>
      </td>
      <td className="px-4 py-3 font-medium text-text-primary">{centsToUsd(expense.amount_cents)}</td>
      <td className="px-4 py-3">
        {expense.is_recurring && (
          <Badge variant="secondary" size="sm">
            Recurring
          </Badge>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <Button
          type="button"
          variant="ghost-danger"
          size="sm"
          onClick={() => void handleDelete()}
          disabled={deleteExpense.isPending}
        >
          Delete
        </Button>
      </td>
    </tr>
  );
}
